import { useEffect, useId, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Undo2 } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { decimalInputToMinor, formatMinorCurrency, minorToDecimalInput } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AdminRefund, RefundEligibility } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input, Textarea } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { Spinner } from '@/components/ui/Spinner'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { createRefund, fetchRefundEligibility } from './api'
import styles from './RefundDialog.module.css'

interface RefundDialogProps {
  /** The booking to refund. Null closes the dialog. */
  tripRequestId: number | null
  onClose: () => void
  /** Called once the backend has confirmed or accepted the refund. */
  onCompleted?: (refund: AdminRefund) => void
}

/** Typing this exactly is the last gate before money moves. */
const CONFIRMATION_WORD = 'REFUND'

/**
 * The refund confirmation dialog.
 *
 * Three rules shape it:
 *
 * 1. **The server decides.** Eligibility, the original amount, the remaining
 *    balance and whether partial refunds are allowed are all fetched, not
 *    inferred. If the fetch says no, the action is not offered at all.
 * 2. **Nothing is optimistic.** The dialog reports what the backend confirmed —
 *    including "accepted but not settled", which is shown as exactly that
 *    rather than as success.
 * 3. **It cannot be submitted twice.** One idempotency key is generated per
 *    opening, the submit button disables itself while in flight, and there is
 *    no automatic retry anywhere in the path.
 */
export function RefundDialog({ tripRequestId, onClose, onCompleted }: RefundDialogProps) {
  const open = tripRequestId !== null
  const queryClient = useQueryClient()
  const warningId = useId()

  const [amountInput, setAmountInput] = useState('')
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitted, setSubmitted] = useState<AdminRefund | null>(null)
  const [idempotencyKey, setIdempotencyKey] = useState('')

  const eligibilityQuery = useQuery({
    queryKey: queryKeys.refunds.eligibility(tripRequestId ?? 0),
    queryFn: () => fetchRefundEligibility(tripRequestId as number),
    enabled: open,
    // Always re-read on open: another administrator may have refunded this
    // booking since the row was rendered.
    staleTime: 0,
    gcTime: 0,
  })

  const eligibility = eligibilityQuery.data

  // A fresh key per opening. Reusing one across openings would make a second,
  // deliberate refund silently return the first one's result.
  useEffect(() => {
    if (!open) return
    setAmountInput('')
    setReason('')
    setNote('')
    setConfirmation('')
    setSubmitted(null)
    setIdempotencyKey(newIdempotencyKey())
  }, [open, tripRequestId])

  // Default the amount to the full remaining balance once the server has said
  // what that is.
  useEffect(() => {
    if (eligibility && eligibility.canRefund) {
      setAmountInput(minorToDecimalInput(eligibility.remainingRefundableAmountMinor))
    }
  }, [eligibility])

  const amountMinor = useMemo(() => decimalInputToMinor(amountInput), [amountInput])

  // How the money actually reaches the customer, decided by the server. An
  // Instapay transfer cannot be reversed the way it arrived, so the approved
  // amount is credited as application points instead — and a supervisor must be
  // told that before they approve, not after.
  const settlesInPoints = eligibility?.refundSettlementChannel === 'POINTS'

  const amountError = useMemo(() => {
    if (!eligibility?.canRefund) return undefined
    if (amountInput.trim() === '') return 'Enter an amount to refund.'
    if (amountMinor === null) return 'Enter a valid amount, for example 250.00.'
    if (amountMinor <= 0) return 'The refund amount must be greater than zero.'
    if (amountMinor > eligibility.remainingRefundableAmountMinor) {
      return `That is more than the ${formatMinorCurrency(
        eligibility.remainingRefundableAmountMinor,
        eligibility.currency,
      )} still refundable.`
    }
    return undefined
  }, [amountInput, amountMinor, eligibility])

  const reasonError = reason.trim().length > 0 && reason.trim().length < 3
    ? 'Give at least a few words — this is recorded against the refund.'
    : undefined

  const mutation = useAdminMutation({
    mutationFn: () =>
      createRefund({
        tripRequestId: tripRequestId as number,
        // Only send an amount when the server allows a partial one; otherwise
        // let the backend refund the full remaining balance it computed.
        amountMinor: eligibility?.partialRefundSupported ? (amountMinor as number) : undefined,
        reason: reason.trim(),
        adminNote: note.trim() === '' ? undefined : note.trim(),
        idempotencyKey,
      }),
    successTitle: 'Refund submitted',
    successDescription: (refund) =>
      refund.status === 'SUCCEEDED'
        ? `${formatMinorCurrency(refund.refundedAmountMinor ?? refund.requestedAmountMinor, refund.currency)} refunded to the customer.`
        : 'Paymob accepted the request but has not settled it yet. Track it in Refunds.',
    errorTitle: 'The refund was not completed',
    invalidate: [
      queryKeys.refunds.all,
      queryKeys.tripRequests.all,
      queryKeys.trips.all,
      queryKeys.dashboard.all,
    ],
    onSuccess: (refund) => {
      setSubmitted(refund)
      if (tripRequestId !== null) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.refunds.eligibility(tripRequestId) })
      }
      onCompleted?.(refund)
    },
  })

  const confirmed = confirmation.trim().toUpperCase() === CONFIRMATION_WORD
  const canSubmit =
    Boolean(eligibility?.canRefund) &&
    !amountError &&
    reason.trim().length >= 3 &&
    confirmed &&
    !mutation.isPending &&
    submitted === null

  const title = submitted ? 'Refund result' : 'Refund this payment'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      busy={mutation.isPending}
      description={
        submitted
          ? undefined
          : settlesInPoints
            ? 'This credits the refund to the customer as application points. It cannot be undone from here.'
            : 'This sends money back to the customer through Paymob. It cannot be undone from here.'
      }
      footer={
        submitted ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              iconLeft={<Undo2 aria-hidden="true" />}
              loading={mutation.isPending}
              disabled={!canSubmit}
              onClick={() => mutation.mutate(undefined)}
            >
              {mutation.isPending
                ? settlesInPoints
                  ? 'Crediting points…'
                  : 'Sending to Paymob…'
                : settlesInPoints
                  ? 'Credit as points'
                  : 'Refund now'}
            </Button>
          </>
        )
      }
    >
      {eligibilityQuery.isLoading ? (
        <div className={styles.loading}>
          <Spinner />
          <p>Checking what can be refunded…</p>
        </div>
      ) : eligibilityQuery.isError ? (
        <div className={styles.blocked} role="alert">
          <AlertTriangle aria-hidden="true" />
          <div>
            <p className={styles.blockedTitle}>Could not check this payment</p>
            <p>
              {eligibilityQuery.error instanceof ApiError
                ? eligibilityQuery.error.message
                : 'The refund eligibility check failed.'}
            </p>
            <Button variant="secondary" size="sm" onClick={() => void eligibilityQuery.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : submitted ? (
        <RefundOutcome refund={submitted} />
      ) : eligibility ? (
        <div className={styles.body}>
          <Summary eligibility={eligibility} />

          {!eligibility.canRefund ? (
            <div className={styles.blocked} role="alert">
              <AlertTriangle aria-hidden="true" />
              <div>
                <p className={styles.blockedTitle}>This payment cannot be refunded</p>
                <p>{eligibility.refundBlockedReason}</p>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.warning} id={warningId} role="note">
                <AlertTriangle aria-hidden="true" />
                {settlesInPoints ? (
                  <p>
                    This booking was paid by Instapay, which cannot be reversed the way it arrived. Confirming
                    credits{' '}
                    <strong>
                      {formatMinorCurrency(
                        // The typed amount while it parses, the remaining
                        // balance while it does not — the figure quoted here
                        // must be the one about to be submitted.
                        amountMinor !== null && amountMinor > 0
                          ? amountMinor
                          : eligibility.remainingRefundableAmountMinor,
                        eligibility.currency,
                      )}
                    </strong>{' '}
                    to {eligibility.customerName ?? 'the customer'} as application points, which they can spend on
                    their next trip. No money is transferred back to their bank.
                  </p>
                ) : (
                  <p>
                    Confirming sends a real refund to Paymob. The money leaves the merchant account immediately and
                    reaches the customer&rsquo;s card in a few business days.
                  </p>
                )}
              </div>

              <Field
                label={eligibility.partialRefundSupported ? 'Refund amount' : 'Refund amount (full only)'}
                required
                error={amountError}
                hint={
                  eligibility.partialRefundSupported
                    ? `Up to ${formatMinorCurrency(
                        eligibility.remainingRefundableAmountMinor,
                        eligibility.currency,
                      )}. Defaults to the full remaining amount.`
                    : 'Partial refunds are disabled for this merchant account.'
                }
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    inputMode="decimal"
                    value={amountInput}
                    readOnly={!eligibility.partialRefundSupported}
                    disabled={mutation.isPending}
                    onChange={(event) => setAmountInput(event.target.value)}
                    addonRight={<span className={styles.currency}>{eligibility.currency}</span>}
                  />
                )}
              </Field>

              <Field
                label="Reason"
                required
                error={reasonError}
                hint="Recorded permanently against this refund and shown in Refund logs."
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    value={reason}
                    maxLength={500}
                    disabled={mutation.isPending}
                    placeholder="Duplicate charge, trip cancelled by operations…"
                    onChange={(event) => setReason(event.target.value)}
                  />
                )}
              </Field>

              <Field label="Internal note" hint="Optional. Never shown to the customer.">
                {({ id, describedBy }) => (
                  <Textarea
                    id={id}
                    aria-describedby={describedBy}
                    rows={2}
                    value={note}
                    maxLength={1000}
                    disabled={mutation.isPending}
                    onChange={(event) => setNote(event.target.value)}
                  />
                )}
              </Field>

              <Field
                label={`Type ${CONFIRMATION_WORD} to confirm`}
                required
                hint="A deliberate step, because this moves real money."
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={confirmation}
                    disabled={mutation.isPending}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={CONFIRMATION_WORD}
                    onChange={(event) => setConfirmation(event.target.value)}
                  />
                )}
              </Field>
            </>
          )}
        </div>
      ) : null}
    </Dialog>
  )
}

/** What is being refunded, in the operator's terms. */
function Summary({ eligibility }: { eligibility: RefundEligibility }) {
  return (
    <section className={styles.summary}>
      <KeyValueGrid>
        <KeyValue label="Customer">
          {eligibility.customerName ?? '—'}
          {eligibility.customerPhone ? <div className={styles.muted}>{eligibility.customerPhone}</div> : null}
        </KeyValue>
        <KeyValue label="Booking">{eligibility.bookingReference ?? `#${eligibility.tripRequestId}`}</KeyValue>
        <KeyValue label="Payment method">{eligibility.paymentMethod ?? '—'}</KeyValue>
        <KeyValue label="Paymob transaction">
          <span className={styles.mono}>{eligibility.paymobTransactionId ?? 'Not recorded'}</span>
        </KeyValue>
        <KeyValue label="Original paid">
          {formatMinorCurrency(eligibility.originalPaidAmountMinor, eligibility.currency)}
        </KeyValue>
        <KeyValue label="Already refunded">
          {formatMinorCurrency(eligibility.successfullyRefundedAmountMinor, eligibility.currency)}
        </KeyValue>
        <KeyValue label="Awaiting settlement">
          {formatMinorCurrency(eligibility.pendingRefundAmountMinor, eligibility.currency)}
        </KeyValue>
        <KeyValue label="Remaining refundable">
          <strong className={styles.remaining}>
            {formatMinorCurrency(eligibility.remainingRefundableAmountMinor, eligibility.currency)}
          </strong>
        </KeyValue>
      </KeyValueGrid>
    </section>
  )
}

/**
 * The result panel.
 *
 * A refund Paymob has not settled is never dressed up as success — an operator
 * who reads "refunded" about money that has not moved is the one who issues a
 * second refund by hand.
 */
function RefundOutcome({ refund }: { refund: AdminRefund }) {
  const settled = refund.status === 'SUCCEEDED'
  // A refund with no gateway transaction behind it was settled in points. The
  // outcome copy has to say so — "reaches their card in a few business days" is
  // simply untrue for an Instapay refund.
  const creditedAsPoints = refund.paymobTransactionId === null

  return (
    <div className={styles.outcome}>
      <div className={styles.outcomeHead}>
        <StatusBadge status={refund.status} />
        <span className={styles.outcomeAmount}>
          {formatMinorCurrency(refund.refundedAmountMinor ?? refund.requestedAmountMinor, refund.currency)}
        </span>
      </div>

      <p className={styles.outcomeMessage}>
        {settled && creditedAsPoints
          ? 'The refund has been credited to the customer as application points. It is available on their next booking.'
          : settled
          ? 'Paymob confirmed the refund. The customer has been notified and it will reach their card within a few business days.'
          : refund.status === 'PENDING'
            ? 'Paymob accepted the refund and is still processing it. It will settle automatically when Paymob confirms — do not send another.'
            : 'Paymob did not confirm the outcome. The attempt is recorded in Refunds; reconcile it there rather than trying again.'}
      </p>

      <KeyValueGrid>
        <KeyValue label="Refund ID">#{refund.id}</KeyValue>
        <KeyValue label="Booking">{refund.bookingReference ?? '—'}</KeyValue>
        {creditedAsPoints ? (
          <KeyValue label="Settled as">Application points</KeyValue>
        ) : (
          <>
            <KeyValue label="Paymob refund transaction">
              <span className={styles.mono}>{refund.paymobRefundTransactionId ?? 'Not issued'}</span>
            </KeyValue>
            <KeyValue label="Gateway response">{refund.gatewayResponseCode ?? '—'}</KeyValue>
          </>
        )}
      </KeyValueGrid>

      {refund.gatewayMessage ? <p className={styles.gatewayMessage}>{refund.gatewayMessage}</p> : null}
    </div>
  )
}

/**
 * A key unique to this dialog opening.
 *
 * `crypto.randomUUID` where it exists; the fallback keeps older browsers and
 * the jsdom test environment working without pulling in a dependency.
 */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `dash-${crypto.randomUUID()}`
  }
  return `dash-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}
